import React from 'react';
import type { OptionType } from '@withjoy/joykit';
import { SelectV1, ButtonV2 } from '@withjoy/joykit';
import { StyledSelectWrapper, StyledAmountButtonsContainer, getAmountButtonOverrides } from './GiftAmount.styles';
import { Input } from '@apps/registry/guest/components/CheckoutDialog/components/FormComponents/Input';
import { FormControl } from '@apps/registry/guest/components/CheckoutDialog/components/FormComponents/FormControl';
import { GiftAmountSelectors } from './GiftAmount.selectors';
import { useGuestRegistryTelemetry } from '@apps/registry/guest/GuestRegistry.telemetry';
import { useTranslation } from '@shared/core';
import type { FormikProps } from 'formik';
import type { GiveAmountDialogFields } from './GiftAmount.types';

// pointerdown covers mouse and touch; cancelling it suppresses the focus change but the click still fires
const preventInputBlur = (event: React.PointerEvent<HTMLButtonElement>) => event.preventDefault();

type DonationAmountInputProps = Readonly<{
  suggestedDonation: number | undefined;
  fieldAmountLabel: string;
  formik: Pick<FormikProps<GiveAmountDialogFields>, 'values' | 'errors' | 'touched' | 'getFieldProps'>;
  stillNeededOptions: OptionType[];
  setAmount: (amount?: string | undefined) => void;
  preventNonNumericalInput: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  currencySymbol: string;
  isDisabled: boolean;
  shouldShowAmountButtons: boolean;
}>;

export const DonationAmountInput = ({
  suggestedDonation,
  fieldAmountLabel,
  formik,
  stillNeededOptions,
  setAmount,
  preventNonNumericalInput,
  currencySymbol,
  isDisabled,
  shouldShowAmountButtons,
}: DonationAmountInputProps) => {
  const hasError = !!formik.errors.amount && !!formik.touched.amount;
  const currentAmount = formik.values.amount;
  const telemetry = useGuestRegistryTelemetry();
  const { tq } = useTranslation('guestRegistry');

  const predefinedAmounts = [100, 200, 300, 400, 500, 1000];

  const handleAmountButtonClick = (amount: number) => {
    telemetry.giftAmountButtonInteracted({ amount });
    setAmount(amount.toString());
  };

  return (
    <>
      {suggestedDonation ? (
        <FormControl
          label={fieldAmountLabel}
          isInvalid={hasError}
          error={hasError ? formik.errors.amount : undefined}
        >
          <StyledSelectWrapper>
            <SelectV1
              {...GiftAmountSelectors.AmountInput}
              disabled={isDisabled}
              defaultValue={stillNeededOptions[0]}
              searchable={false}
              options={stillNeededOptions}
              fluid={false}
              onChange={e => setAmount(e?.value)}
            />
          </StyledSelectWrapper>
        </FormControl>
      ) : (
        <>
          {shouldShowAmountButtons && (
            <StyledAmountButtonsContainer
              gap={3}
              marginTop={3}
              marginBottom={5}
              role="group"
              aria-label={tq`Quick amount selection`}
            >
              {predefinedAmounts.map(amount => {
                const isSelected = !!(currentAmount && Number(currentAmount) === amount);
                return (
                  <ButtonV2
                    key={amount}
                    variant="outline"
                    intent="neutral"
                    size="md"
                    disabled={isDisabled}
                    aria-pressed={isSelected}
                    // keep focus in the autofocused amount input: a blur would mark it touched and flash the "required" error until the click lands
                    onPointerDown={preventInputBlur}
                    onClick={() => handleAmountButtonClick(amount)}
                    overrides={getAmountButtonOverrides(isSelected)}
                  >
                    {currencySymbol}
                    {amount}
                  </ButtonV2>
                );
              })}
            </StyledAmountButtonsContainer>
          )}
          <FormControl
            label={fieldAmountLabel}
            isInvalid={hasError}
            error={hasError ? formik.errors.amount : undefined}
          >
            <Input
              {...GiftAmountSelectors.AmountInput}
              startElement={currencySymbol}
              isInvalid={hasError}
              {...formik.getFieldProps('amount')}
              type="number"
              onKeyDown={preventNonNumericalInput}
              tabIndex={0}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              disabled={isDisabled}
            />
          </FormControl>
        </>
      )}
    </>
  );
};
